import { useState } from 'react';
import PropTypes from 'prop-types';
import { matchPath, useLocation, useHistory } from 'react-router-dom';
import {
  Badge, Collapse, List, ListItemButton, ListItemIcon, ListItemText, Box, Tooltip
} from '@mui/material';
import { useSelector } from 'react-redux';
import { alpha } from '@mui/material/styles';

// Material UI Icons
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import FiberManualRecord from '@mui/icons-material/FiberManualRecord';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import SettingsInputComponentIcon from '@mui/icons-material/SettingsInputComponent';
import ListAltIcon from '@mui/icons-material/ListAlt';
import AddCardIcon from '@mui/icons-material/AddCard';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import AssessmentIcon from '@mui/icons-material/Assessment';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import BusinessIcon from '@mui/icons-material/Business';
import SummarizeIcon from '@mui/icons-material/Summarize';
import BuildIcon from '@mui/icons-material/Build';
import SettingsIcon from '@mui/icons-material/Settings';
import DashboardIcon from '@mui/icons-material/Dashboard';
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation';
import GroupsIcon from '@mui/icons-material/Groups';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import BadgeIcon from '@mui/icons-material/Badge';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import CarRepairIcon from '@mui/icons-material/CarRepair';

const ICON_MAPPING = {
  dashboard: DashboardIcon,
  sites: BusinessIcon,
  tanks: LocalGasStationIcon,
  'vehicle management': LocalShippingIcon,
  vehicles: DirectionsCarIcon,
  reports: AssessmentIcon,
  report: AssessmentIcon,
  'user management': ManageAccountsIcon,
  settings: SettingsIcon,
  cards: CreditCardIcon,
  transactions: PointOfSaleIcon,
  groups: GroupsIcon,
  'tyre management': CarRepairIcon,
  tyres: CarRepairIcon,
  tyre: CarRepairIcon,
  list: ListAltIcon,
  new: AddCardIcon,
  records: SummarizeIcon,
  breakdown: BuildIcon,
  reconciliation: ReceiptLongIcon,
  sales: PointOfSaleIcon,
  approvals: SettingsInputComponentIcon,
  approvers: FactCheckIcon,
  requestors: AssignmentIndIcon,
  requesters: AssignmentIndIcon,
  'site managers': SupervisorAccountIcon,
  'site supervisors': SupervisorAccountIcon,
  operators: BadgeIcon,
  drivers: DirectionsCarIcon
};

const getIconForTitle = (title) => {
  if (!title) return null;
  const normalizedTitle = title.toLowerCase().trim();
  if (ICON_MAPPING[normalizedTitle]) {
    return ICON_MAPPING[normalizedTitle];
  }
  const foundKey = Object.keys(ICON_MAPPING).find((key) => {
    if (key.length < 4) return false;
    return normalizedTitle.includes(key);
  });
  if (foundKey) return ICON_MAPPING[foundKey];
  return null;
};

const NavItem = ({ item, collapsed = false, onToggleCollapse }) => {
  const { href, title, badge } = item;
  const Icon = getIconForTitle(title) || item.icon;
  const history = useHistory();
  const location = useLocation();
  const { pendingApprovalsCount } = useSelector((state) => state.cardApprovals);
  const [open, setOpen] = useState(false);

  const isActivePath = (path = href) => (
    !!path && !!matchPath(location.pathname, { path, end: false })
  );

  const isChildActive = item.subMenu
    ? item.subMenu.some((child) => isActivePath(child.href))
    : false;

  const isActive = isActivePath() || isChildActive;

  const navigateToPath = (path = href) => {
    if (!path) return;
    const pathInfo = { pathname: path, state: { fromDashboard: true } };
    history.push(pathInfo);
  };

  const handleMenuClick = () => {
    if (collapsed) onToggleCollapse();
    if (item.subMenu && !collapsed) {
      setOpen(!open);
    } else if (!item.subMenu) {
      navigateToPath();
    }
  };

  if (item.hidden) return null;

  const activeColor = '#10B981';
  const inactiveColor = 'rgba(255,255,255,0.7)';
  const hoverBg = alpha('#ffffff', 0.08);

  return (
    <>
      <Tooltip title={collapsed ? title : ''} placement="right" arrow disableHoverListener={!collapsed}>
        <ListItemButton
          disableGutters
          onClick={handleMenuClick}
          sx={{
            py: 1.25,
            px: collapsed ? 1.5 : 2,
            mb: 0.5,
            borderRadius: 1,
            justifyContent: collapsed ? 'center' : 'flex-start',
            color: isActive ? activeColor : inactiveColor,
            bgcolor: isActive ? alpha(activeColor, 0.12) : 'transparent',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              bgcolor: isActive ? alpha(activeColor, 0.18) : hoverBg,
              color: isActive ? activeColor : '#ffffff'
            },
            '&::before': isActive && !collapsed ? {
              content: '""',
              position: 'absolute',
              left: 0,
              top: '50%',
              transform: 'translateY(-50%)',
              height: 24,
              width: 4,
              backgroundColor: activeColor,
              borderRadius: '0 4px 4px 0',
              boxShadow: `0 0 8px ${activeColor}`
            } : {}
          }}
        >
          {Icon && (
            <ListItemIcon sx={{
              minWidth: collapsed ? 0 : 36,
              justifyContent: 'center',
              color: isActive ? activeColor : inactiveColor
            }}
            >
              <Icon fontSize="small" sx={{ color: isActive ? activeColor : inactiveColor }} />
            </ListItemIcon>
          )}
          {!collapsed && (
            <ListItemText primary={title} primaryTypographyProps={{ variant: 'body2', fontWeight: isActive ? 700 : 500, fontSize: '0.9rem' }} />
          )}
          {!collapsed && badge && (
            <Badge badgeContent={pendingApprovalsCount} color="error" sx={{ '& .MuiBadge-badge': { right: -10, top: 0, border: '2px solid #1F2937' } }} />
          )}
          {!collapsed && item.subMenu && (
            <Box sx={{ color: 'rgba(255,255,255,0.4)' }}>
              {open ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
            </Box>
          )}
        </ListItemButton>
      </Tooltip>

      {!collapsed && item.subMenu && (
        <Collapse in={open} timeout="auto" unmountOnExit>
          <List component="div" disablePadding sx={{ position: 'relative', mt: 0.5 }}>
            <Box sx={{
              position: 'absolute',
              left: 28,
              top: 0,
              bottom: 10,
              width: '1px',
              bgcolor: 'rgba(255,255,255,0.1)'
            }}
            />
            {item.subMenu.map((subMenu) => {
              const isSubActive = isActivePath(subMenu.href);
              let SubIcon = getIconForTitle(subMenu.title) || subMenu.icon;
              if (!SubIcon) SubIcon = FiberManualRecord;
              return !subMenu.hidden && (
                <ListItemButton
                  key={subMenu.title + subMenu.href}
                  onClick={() => navigateToPath(subMenu.href)}
                  sx={{
                    py: 1,
                    pl: 5.5,
                    pr: 2,
                    mb: 0.25,
                    borderRadius: 1,
                    color: isSubActive ? '#ffffff' : 'rgba(255,255,255,0.6)',
                    bgcolor: isSubActive ? alpha('#ffffff', 0.08) : 'transparent',
                    '&:hover': { color: '#ffffff', bgcolor: alpha('#ffffff', 0.04) }
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 24, mr: 1, color: 'inherit' }}>
                    <SubIcon sx={{ fontSize: '18px', color: isSubActive ? activeColor : 'inherit', transition: 'all 0.2s' }} />
                  </ListItemIcon>
                  <ListItemText primary={subMenu.title} primaryTypographyProps={{ variant: 'body2', fontSize: '0.85rem', fontWeight: isSubActive ? 500 : 400 }} />
                </ListItemButton>
              );
            })}
          </List>
        </Collapse>
      )}
    </>
  );
};

NavItem.propTypes = {
  item: PropTypes.object.isRequired,
  collapsed: PropTypes.bool,
  onToggleCollapse: PropTypes.func
};

export default NavItem;
